import { Injectable } from '@angular/core';
import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class InsightsService {
  private readonly appInsights: ApplicationInsights;
  private readonly isConfigured: boolean;

  constructor() {
    // Verifica se a connection string é válida (não é um placeholder da pipeline)
    const connString = environment.APPINSIGHTS_CONNECTION_STRING;
    this.isConfigured = !!connString && 
                        !connString.startsWith('#{') && 
                        !connString.endsWith('}#') &&
                        !connString.startsWith('$(');  // variável ADO não expandida na pipeline

    this.appInsights = new ApplicationInsights({
      config: {
        connectionString: this.isConfigured ? connString : undefined,
        enableAutoRouteTracking: true,   // rastreia mudanças de rota Angular automaticamente
        enableCorsCorrelation: true,      // propaga correlation IDs para a API
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        disableTelemetry: !this.isConfigured // desativa se não estiver configurado
      }
    });

    if (this.isConfigured) {
      try {
        this.appInsights.loadAppInsights();
        this.trackTrace('insights_initialized', undefined, {
          app: 'ctinvoic-ui'
        });
      } catch (e) {
        console.info('[InsightsService] Failed to initialize Application Insights:', e);
        this.isConfigured = false;
      }
    } else {
      console.info('Application Insights desabilitado: connection string não configurada.');
    }
  }

  /** Define o usuário autenticado no contexto de telemetria */
  setAuthenticatedUser(userId: string, accountId?: string): void {
    if (!this.isConfigured) { return; }
    this.appInsights.setAuthenticatedUserContext(userId, accountId, true);
  }

  /** Remove o contexto de usuário (logout) */
  clearUser(): void {
    if (!this.isConfigured) { return; }
    this.appInsights.clearAuthenticatedUserContext();
  }

  /** Rastreia uma exceção manualmente */
  trackException(error: Error, severityLevel?: SeverityLevel): void {
    if (!this.isConfigured) { return; }
    this.appInsights.trackException({ exception: error, severityLevel });
  }

  /** Rastreia um evento de negócio customizado */
  trackEvent(name: string, properties?: Record<string, string>): void {
    if (!this.isConfigured) { return; }
    this.appInsights.trackEvent({ name }, properties);
  }

  /** Rastreia uma mensagem de trace (popula a tabela AppTraces no Log Analytics) */
  trackTrace(message: string, severityLevel?: SeverityLevel, properties?: Record<string, string>): void {
    if (!this.isConfigured) { return; }
    this.appInsights.trackTrace({ message, severityLevel }, properties);
  }

  /** Rastreia um heartbeat técnico para manter sinal de telemetria no AppTraces */
  trackHeartbeat(properties?: Record<string, string>): void {
    if (!this.isConfigured) { return; }
    this.trackTrace('ui_heartbeat', undefined, properties);
  }
}
